import { AlertMessageItem, AlertType } from '@lib/interfaces';
import { Injectable } from '@angular/core';
import {
  GetOrderResponseData,
  GetSeatmapsResponseDataSeatmapsDecksInner,
  GetSeatmapsResponseDataSeatmapsDecksInnerSeatsRowsInner,
  OrderSeatItemSeatSelection,
} from 'src/sdk-servicing';
import { GetSeatmapsState } from '@common/store';
import { SeatInfo, SeatInfoPassengers } from '@common/interfaces/servicing-seatmap/seat-info';
import { SeatForServicingSeatmapScreen } from '@common/interfaces/servicing-seatmap/seatmap-for-seatmap-screen';
import { SeatAvailability } from '@common/interfaces/servicing-seatmap/seat-availability';
import { PREVIOUS_PAGE_IDENTIFIER } from '@common/interfaces';
import { splitCouchSeatNumber } from '@common/helper';

@Injectable({
  providedIn: 'root',
})
export class ServicingSeatmapCommonErrorService {
  // [以下、初期ワーニング表示処理]
  public InitialWarningDisplay(
    seatInformationMap: Map<string, SeatForServicingSeatmapScreen>,
    pnrInfo: GetOrderResponseData,
    displayTargetBoundArrayId: number,
    displayTargetSegmentArrayId: number,
    getSeatmaps?: GetSeatmapsState,
    previousPageIdentifier?: PREVIOUS_PAGE_IDENTIFIER,
    isReDisplaySeatmap?: boolean,
    displayTargetSegmentId?: string,
    selectedSeatInfoList?: SeatInfo[] | undefined,
    documentNumberEtktPrefixNh?: Array<string>,
    nhGroupOperatingList?: Array<string>
  ): {
    messages: AlertMessageItem[];
    selectingSeatInfoList?: SeatInfo[];
    seatInformationMap?: Map<string, SeatForServicingSeatmapScreen>;
  } {
    // ワーニングメッセージが複数表示できるように配列として保持・返却する
    let messages: AlertMessageItem[] = [];

    // 9.6.1.1	PNR情報.data.air.isContainedSeatRequestableSegmentInLHSite=trueかつPNR情報.data.air.bounds[表示対象バウンド配列ID].flights[表示対象セグメント配列ID].運航キャリアコード='LH'の場合、
    // ”W0585”(予約詳細画面(S01-P030)からLHサイトへ遷移し座席指定可能な旨)のワーニングメッセージを表示する。
    if (
      pnrInfo.air?.isContainedSeatRequestableSegmentInLHSite === true &&
      pnrInfo.air?.bounds![displayTargetBoundArrayId].flights![displayTargetSegmentArrayId].operatingAirlineCode ===
        'LH'
    ) {
      messages.push({
        contentHtml: 'W0585',
        isCloseEnable: true,
        errorMessageId: 'W0585',
      });

      // 9.6.1.2	上記以外、かつシートマップ情報.data.seatmapFlightProperty.isFullyBlockedByAllChargeableOAFlight=trueかつ、PNR情報.data.air.bounds[表示対象バウンド配列ID].flights[表示対象セグメント配列ID].運航キャリアコード≠”NH”または"NQ"または"EH"の場合、
      // ”W0579”(有料席ありのため選択不可と表示している可能性がある旨)のワーニングメッセージを表示する。
    } else if (
      getSeatmaps?.model?.data.seatmapFlightProperty.isFullyBlockedByAllChargeableOAFlight === true &&
      !this._isNhGroupOperating(pnrInfo, nhGroupOperatingList, displayTargetBoundArrayId, displayTargetSegmentArrayId)
    ) {
      messages.push({
        contentHtml: 'W0579',
        isCloseEnable: true,
        errorMessageId: 'W0579',
      });

      // 9.6.1.4	上記以外の場合、以下の処理を行う。
      //PNR情報.data.air.bounds[表示対象バウンド配列ID].flights[表示対象セグメント配列ID].運航キャリアコード≠”NH”または"NQ"または"EH"の場合エラーメッセージIDに”W0580”(他社運航である旨) のワーニングメッセージを表示する。
    } else {
      if (
        !this._isNhGroupOperating(pnrInfo, nhGroupOperatingList, displayTargetBoundArrayId, displayTargetSegmentArrayId)
      ) {
        messages.push({
          contentHtml: 'W0580',
          isCloseEnable: true,
          errorMessageId: 'W0580',
        });
      }
      // シートマップ情報.data.travelersSummary.hasCouchDiscrepancy=false、かつシートマップ情報.data.seatmaps.numberOfAvailableSeats=0
      // の場合、エラーメッセージIDに”W0571”(座席登録ができない旨) のワーニングメッセージを表示する。
      if (
        !!getSeatmaps?.model?.data.travelersSummary &&
        getSeatmaps?.model?.data.travelersSummary.hasCouchDiscrepancy === false &&
        getSeatmaps?.model?.data.seatmaps.numberOfAvailableSeats === 0
      ) {
        messages.push({
          contentHtml: 'W0571',
          isCloseEnable: true,
          errorMessageId: 'W0571',
        });
      }
    }

    // 7.6.7	シートマップ情報.data.seatmaps.numberOfAvailableSeats>0、かつ
    // シートマップ情報.data.seatmaps.numberOfAvailableSeats<PNR情報.data.travelersSummary.travelerNumbersの
    // INFを除く合計-PNR情報.seats.＜当該表示対象セグメントID＞.orderseatItemsの要素数である場合、
    // ”W0572”(一部搭乗者について座席登録ができない旨) のワーニングメッセージを表示する。
    let travelersTotal =
      (pnrInfo.travelersSummary?.travelerNumbers?.total || 0) - (pnrInfo.travelersSummary?.travelerNumbers?.INF || 0);
    let seatTotal = 0;
    if (!!displayTargetSegmentId) {
      pnrInfo.travelers?.forEach((traveler) => {
        let seatSelection: OrderSeatItemSeatSelection =
          pnrInfo.seats?.[displayTargetSegmentId]?.[traveler.id!]?.seatSelection;
        if (!!seatSelection && !!seatSelection.seatNumbers) {
          seatTotal += seatSelection.seatNumbers.length;
        }
      });
    }

    if (
      !!getSeatmaps?.model?.data.seatmaps.numberOfAvailableSeats &&
      getSeatmaps?.model?.data.seatmaps.numberOfAvailableSeats < travelersTotal - seatTotal
    ) {
      messages.push({
        contentHtml: 'W0572',
        isCloseEnable: true,
        errorMessageId: 'W0572',
      });
    }

    // デッキ情報に含まれる全てのrowsを取得する
    const allRows = this._getAllRowsFromDecks(getSeatmaps?.model?.data.seatmaps.decks);

    // 座席の再選択を促す旨
    if (!!selectedSeatInfoList && !!allRows) {
      // ＜以下、選択中座席情報.表示対象セグメントIDの要素数分、繰り返し＞
      let passengerList = selectedSeatInfoList.find((s) => s.segmentId === displayTargetSegmentId)?.passengerList;

      if (!!passengerList) {
        let isReselection = false;
        for (let passenger of passengerList) {
          let seatRowNumber: number;
          let seatNumbers: string[];
          // ＜以下、シートマップ情報.data.seatmap.decks.seat.rows(以降、座席行とする)の要素数分、繰り返し＞
          for (let row of allRows) {
            seatNumbers = [];
            // 選択中座席情報リストから座席番号を取得
            let seatNumberList = passenger?.seatNumberList;
            seatNumbers.push(!!seatNumberList && seatNumberList.length > 0 ? seatNumberList[0].seatNumber : '');

            // SSR登録済みの座席を取得する。
            let ssrSeatNumbers: string[] = !!pnrInfo?.seats?.[displayTargetSegmentId || '']?.[passenger.id || '']
              ? pnrInfo?.seats?.[displayTargetSegmentId || '']?.[passenger.id || ''].seatSelection.seatNumbers
              : '';
            let ssrSeatNumber = ssrSeatNumbers && ssrSeatNumbers.length > 0 ? ssrSeatNumbers[0] : '';
            if (seatInformationMap.get(ssrSeatNumber)?.isCouchSeat) {
              const { rowNumber, columnNumbers } = splitCouchSeatNumber(
                seatInformationMap.get(ssrSeatNumber)?.couchNumber as string
              );

              if (!!rowNumber && !!columnNumbers) {
                seatNumbers = [];
                for (let columnNumber of columnNumbers) {
                  seatNumbers.push(`${rowNumber}${columnNumber}`);
                }
              }
            }

            // 座席を変更した場合のみ後続の判定を実施する（取消は除く）
            // 「座席を変更している、かつ、SSR登録済み座席番号と選択中座席番号が一致しない場合」が該当
            const isSelectedSeatNumber: boolean =
              !!seatNumberList && seatNumberList.length > 0 && seatNumberList[0].seatNumber.length > 0;
            const isChangedSeat: boolean =
              isSelectedSeatNumber && (!!seatNumbers.find((n) => n !== ssrSeatNumber) || false);

            if (!!passenger && !!seatNumbers && isChangedSeat) {
              // 7.6.8.1	選択中座席情報.当該セグメント.当該搭乗者.座席番号の下1桁以外を座席行番号として保持する。
              seatRowNumber = Number(seatNumbers[0].substring(0, seatNumbers[0].length - 1));

              // 7.6.8.2	座席行.当該rowNumber=座席行番号の場合、座席選択不可判定処理として以下の処理を行う。
              // 座席行番号を数値で比較する
              if (Number(row.rowNumber) === seatRowNumber && row.rowType === 'seat') {
                // 7.6.8.2.1	＜座席行.当該rowNumberの要素数分、繰り返し＞
                for (let col of row.columns) {
                  // 7.6.8.2.2	座席行.当該rowNumber.当該columns.seatNumber=選択中座席情報.表示対象セグメントID.当該選択中の座席情報.座席番号の場合、
                  //以下の処理を行う。
                  if (seatNumbers.find((n) => n === col.seatNumber)) {
                    // 選択している座席が登録済みか判定
                    const isSelectedSeat = ssrSeatNumber && !!seatNumbers.find((n) => n === ssrSeatNumber);
                    // 選択している座席が有料またはカウチ席の場合、取消と再選択は同時にできないため考慮不要
                    if (isSelectedSeat && (col.isChargeableAsrSeat || col.isCouchSeat)) {
                      continue;
                    }

                    // 座席利用可否情報を取得する。カウチの場合は各座席の状態を考慮して判定する。
                    let tmpSeatAvailabilityStatus = col.seatAvailabilityStatus;
                    if (col.isCouchSeat) {
                      const { rowNumber, columnNumbers } = splitCouchSeatNumber(
                        seatInformationMap?.get(col.seatNumber)?.couchNumber as string
                      );
                      if (!!rowNumber && !!columnNumbers) {
                        for (const columnNumber of columnNumbers) {
                          const tmpSeatNumber = `${rowNumber}${columnNumber}`;
                          if (
                            row.columns.find((c) => c !== null && c.seatNumber === tmpSeatNumber)
                              ?.seatAvailabilityStatus === 'occupied'
                          ) {
                            tmpSeatAvailabilityStatus = 'occupied';
                            break;
                          } else if (
                            row.columns.find((c) => c !== null && c.seatNumber === tmpSeatNumber)
                              ?.seatAvailabilityStatus === 'blocked'
                          ) {
                            tmpSeatAvailabilityStatus = 'blocked';
                            break;
                          }
                        }
                      }
                    }

                    // seatAvailabilityStatusがavailable以外の場合は後続処理に進む
                    // 前処理で座席変更した場合のみ判定するフローとなるので、自分自身のoccupiedは考慮不要
                    if (tmpSeatAvailabilityStatus !== 'available') {
                      isReselection = true;
                      
                      // 7.6.8.2.2.1.2	選択中座席情報.表示対象セグメントID.当該選択している座席情報に""(空欄)を設定する。
                      selectedSeatInfoList = this._setPassengerSeatNumber(
                        selectedSeatInfoList,
                        passenger.id,
                        displayTargetSegmentId || ''
                      );
                      let seatmaps = seatInformationMap;
                      seatmaps.forEach((seat, seatNumber) => {
                        let travelers = seat.travelers?.map((traveler) => {
                          let isClickable = !seatNumbers.find((n) => n === seatNumber) ? true : false;
                          return {
                            id: traveler.id,
                            seatAvailabilityStatus: traveler.seatAvailabilityStatus,
                            isClickable: traveler.id === passenger.id ? isClickable : traveler.isClickable,
                            prices: traveler.prices,
                            isExempted: traveler.isExempted,
                          } as SeatAvailability;
                        });

                        seatInformationMap.set(seatNumber, {
                          blockInformation: seat?.blockInformation,
                          columnArrayID: seat?.columnArrayID,
                          columnNumber: seat?.columnNumber,
                          couchNumber: seat?.couchNumber,
                          deckType: seat?.deckType,
                          isChargeableAsrSeat: seat?.isChargeableAsrSeat,
                          isChildSeatAttachable: seat?.isChildSeatAttachable!,
                          isCouchSeat: seat?.isCouchSeat,
                          isRearDirectionSeat: seat?.isRearDirectionSeat,
                          isSpecifiedStatus: seat?.isSpecifiedStatus,
                          registedPassengerID: seat?.registedPassengerID,
                          rowArrayID: seat?.rowArrayID,
                          rowNumber: seat?.rowNumber,
                          seatCharacteristicsCodes: seat?.seatCharacteristicsCodes,
                          selectingPassengerID: seat?.selectingPassengerID,
                          specifiedPassengerID: seat?.specifiedPassengerID,
                          travelers: travelers,
                          prices: seat?.prices,
                        });
                      });
                    }
                    // 7.6.8.2.3	上記以外の場合、次の座席行.当該rowNumberの繰り返しを行う。
                    else {
                      continue;
                    }
                  }
                } // ＜ここまで、座席行.当該rowNumberの要素数分、繰り返し＞
              }
              // 7.6.8.3	上記以外の場合、次の選択中座席情報.表示対象セグメントIDの繰り返し処理を行う。
              else if (row.rowNumber > seatRowNumber) {
                break;
              }
            }
          } // ＜ここまで、シートマップ情報.data.seatmap.decks.seats.rowsの要素数分、繰り返し＞
        } // ＜ここまで、選択中座席情報.＜表示対象セグメントID＞の要素数分、繰り返し＞
        if (isReselection) {
          // ”W0575” (座席の再選択を促す旨)のワーニングメッセージを表示し処理を終了する
          messages.push({
            contentHtml: 'W0575',
            isCloseEnable: true,
            errorMessageId: 'W0575',
          });
        }
      }
    }

    // 9.6.5	登録座席行情報存在判定処理として、以下の処理を行う。
    // 9.6.5.1	登録座席行情報存在フラグにtrueを設定する。
    let registeredSeatRowInfoPresenceFlag: boolean = true;
    let passengers =
      !!selectedSeatInfoList && selectedSeatInfoList.find((s) => s.segmentId === displayTargetSegmentId)?.passengerList;

      if (!!passengers && !!displayTargetSegmentId && !!allRows) {
      // 登録済み座席数
      let selectedSeatsCount = 0;
      // 登録済み座席が存在する件数
      let seatExistenceCount = 0;

      // ＜以下、PNR情報.seats.表示対象セグメントIDの要素数分、繰り返し＞
      for (let passenger of passengers) {
        // 登録済み座席数と登録済み座席が存在する件数が異なる場合、登録座席行情報存在フラグに繰り返し処理を終了する。
        if (selectedSeatsCount !== seatExistenceCount) {
          break;
        }

        let seatSelection: OrderSeatItemSeatSelection =
          pnrInfo.seats?.[displayTargetSegmentId]?.[passenger.id]?.seatSelection;

        if (!!seatSelection && !!seatSelection.seatNumbers && seatSelection.seatNumbers.length > 0) {
          // 登録済み座席数を+1する
          selectedSeatsCount++;

          const seatNumber = seatSelection.seatNumbers[0];
          const regexNumber = /^[0-9]+/g;
          const rowNumbers = seatNumber?.match(regexNumber);
          // ＜以下、シートマップ情報.data.seatmap.decks.seats.rows（以下、行情報リストとする）の要素数分、繰り返し＞
          for (let row of allRows) {
            // 指定済みの座席がシートマップ情報に存在する場合、登録済み座席が存在する件数を+1する
            if (
              !!rowNumbers &&
              Number(rowNumbers) === Number(row.rowNumber) &&
              row.rowType === 'seat' &&
              row.columns?.find((c) => c.columnType === 'seat' && c.seatNumber === seatNumber)
            ) {
              seatExistenceCount++;
              break;
            }
          }
          // ＜ここまで、行情報リストの要素数分、繰り返し＞
        }
      } // ＜ここまで、PNR情報.seats.表示対象セグメントIDの要素数分、繰り返し＞

      // 登録済み座席数と登録済み座席が存在する件数が異なる場合、登録座席行情報存在フラグにfalseを設定する。
      if (selectedSeatsCount !== seatExistenceCount) {
        registeredSeatRowInfoPresenceFlag = false;
      }
    }

    // 9.6.5.2	登録座席行情報存在フラグ=falseの場合、”W0584”(登録済みの座席行が表示されていない旨)のワーニングメッセージを表示する。
    if (registeredSeatRowInfoPresenceFlag === false) {
      messages.push({
        contentHtml: 'W0584',
        isCloseEnable: true,
        errorMessageId: 'W0584',
      });
    }

    // 7.6.10	遷移元機能IDがサービシング領域のIDであれば、サービシングフローワーニング表示判定処理として、以下の処理を行う。
    let isServicingPrevPage = Object.entries(PREVIOUS_PAGE_IDENTIFIER).some(
      (item) => item[1] === previousPageIdentifier
    );
    if (isServicingPrevPage) {
      // 7.6.10.1	シートマップ情報.data.travelersSummary.hasCouchDiscrepancy=trueの場合、
      // ”W0581”(処理中のため座席の指定が行えない搭乗者が存在する旨)のワーニングメッセージを表示する。
      if (getSeatmaps?.model?.data.travelersSummary.hasCouchDiscrepancy === true) {
        messages.push({
          contentHtml: 'W0581',
          isCloseEnable: true,
          errorMessageId: 'W0581',
        });
      }

      // 7.6.10.2	以下の条件を全て満たす場合、後続のエラーメッセージ表示処理を行う。
      // 7.6.10.2.1	再読み込みかどうか=false
      // 7.6.10.2.2	PNR情報.data.seats.isContainedCouchFlight=true、またはPNR情報.data.seats.isChargeableSeatFlight=true
      if (
        !isReDisplaySeatmap &&
        (pnrInfo.seats?.isContainedCouchFlight === true || pnrInfo.seats?.isChargeableSeatFlight === true)
      ) {
        // PNR情報.data.travelDocumentsの中に上3桁が「205(NH発券航空券番号プレフィックス)」ではない航空券番号を持つ旅客が存在する場合、
        // ”W0573”(この予約では有料ASR席、カウチ席は指定不可である旨)のワーニングメッセージを表示する。
        if (this._isOtherDocumentNumberNh(pnrInfo, documentNumberEtktPrefixNh)) {
          messages.push({
            contentHtml: 'W0573',
            isCloseEnable: true,
            errorMessageId: 'W0573',
          });
        }
        // 7.6.10.4	PNR情報.data.orderStatus=”unticketed”の場合、”W0574”(有料ASR席、カウチ席は指定不可である旨)のワーニングメッセージを表示する。
        if (pnrInfo.orderStatus === 'unticketed') {
          messages.push({
            contentHtml: 'W0574',
            isCloseEnable: true,
            errorMessageId: 'W0574',
          });
        }
      }
    }
    // [ここまで、初期ワーニング表示処理]
    return {
      messages: messages,
      selectingSeatInfoList: selectedSeatInfoList,
      seatInformationMap: seatInformationMap,
    };
  }

  /**
   * 選択中座席情報リスト.搭乗者リスト.座席番号リストを空で更新する。
   * @param selectedSeatInfoList 選択中座席情報リスト
   * @param selectingPassengerId 選択中搭乗者ID
   * @param displayTargetSegmentId 表示対象セグメントID
   * @returns 更新後の選択中座席情報リスト
   */
  private _setPassengerSeatNumber(
    selectedSeatInfoList: SeatInfo[],
    selectingPassengerId: string,
    displayTargetSegmentId: string
  ) {
    const newSeatInfoList = selectedSeatInfoList.map((seatInfo) => {
      const newPassengerList: SeatInfoPassengers[] = seatInfo.passengerList.map((value) => {
        if (seatInfo.segmentId !== displayTargetSegmentId || value.id !== selectingPassengerId) {
          return value;
        }

        return {
          id: value.id,
          seatNumberList: [],
          couchSeatNumberList: [],
          ssrInformation: undefined,
          specifiedAmount: 0,
          hasBassinetAttribute: value.hasBassinetAttribute,
          isChildSeatSelectable: value.isChildSeatSelectable,
          isSelectable: true,
        };
      });
      return {
        segmentId: seatInfo.segmentId,
        numberOfChildSeats: seatInfo.numberOfChildSeats,
        passengerList: newPassengerList,
        characteristicRequestSsrCode: seatInfo.characteristicRequestSsrCode || '',
      };
    });

    return newSeatInfoList;
  }

  /**
   * NH発券(航空券番号の先頭3文字がNH発券の値)以外が存在するかどうか
   *
   * @param pnrInfo PNR情報
   * @param documentNumberEtktPrefixNh NH発券航空券番号プレフィックスリスト
   * @returns 判定結果
   */
  private _isOtherDocumentNumberNh(
    pnrInfo: GetOrderResponseData,
    documentNumberEtktPrefixNh: Array<string> | undefined
  ) {
    if (!!pnrInfo.travelDocuments && !!documentNumberEtktPrefixNh && documentNumberEtktPrefixNh.length >= 1) {
      return pnrInfo.travelDocuments.some(
        (td) =>
          td.documentType === 'eticket' &&
          documentNumberEtktPrefixNh.some((prefixNh) => prefixNh !== td.id?.substring(0, 3))
      );
    }
    return false;
  }

  /**
   * NHグループ運航かどうか
   *
   * @param pnrInfo PNR情報
   * @param nhGroupOperatingList NHグループ運航キャリアリスト
   * @param displayTargetBoundArrayId 表示対象バウンド配列ID
   * @param displayTargetSegmentArrayId 表示対象セグメント配列ID
   * @returns true: NHグループの場合、false: それ以外
   */
  private _isNhGroupOperating(
    pnrInfo: GetOrderResponseData,
    nhGroupOperatingList: Array<string> | undefined,
    displayTargetBoundArrayId: number,
    displayTargetSegmentArrayId: number
  ) {
    let operatingAirlineCode: string =
      pnrInfo.air?.bounds![displayTargetBoundArrayId].flights![displayTargetSegmentArrayId].operatingAirlineCode || '';
    if (nhGroupOperatingList?.includes(operatingAirlineCode)) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * 各デッキに含まれる全てのrowsを取得する
   * @param decks デッキ情報
   * @returns デッキ情報に含まれるrowsをすべて結合した配列。取得できなければ空の配列。
   */
  private _getAllRowsFromDecks(
    decks: Array<GetSeatmapsResponseDataSeatmapsDecksInner> | undefined
  ): Array<GetSeatmapsResponseDataSeatmapsDecksInnerSeatsRowsInner> {
    let rows: Array<GetSeatmapsResponseDataSeatmapsDecksInnerSeatsRowsInner> = [];
    decks?.forEach((deck) => {
      rows = rows.concat(deck.seats.rows);
    });
    return rows;
  }
}
